import { Result } from 'antd';
import 'antd/dist/antd.css';
import LoadingSpinner from 'components/LoadingSpinner';
import LotModal from 'components/modal/LotModal';
import SiteMapPage from 'features/SiteMapPage';
import { setUserData } from 'features/siteMapSlice';
import config from 'lib/config';
import {
  DEFAULT_BG_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_HIGHLIGHT_COLOR,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_PRIMARY_TEXT_COLOR,
  DEFAULT_SECONDARY_COLOR,
  PARTNER_TOKENS,
} from 'lib/constants';
import GlobalStyle from 'lib/globalStyles';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useEffect } from 'react';
import 'react-multi-carousel/lib/styles.css';
import { DefaultTheme, ThemeProvider } from 'styled-components';
import FloorPlanModal from 'components/modal/FloorPlanModal';
import UTourModal from 'components/modal/UTourModal';

export default function App() {
  const dispatch = useAppDispatch();
  const { currPlanCode, currUTourUrl } = useAppSelector((state) => ({
    currPlanCode: state.siteMap.currPlanCode,
    currUTourUrl: state.siteMap.currUTourUrl,
  }));

  // get URL params
  const URLParams = new URLSearchParams(window.location.search);
  const communityId = URLParams.get('communityId');
  const sessionId = URLParams.get('sessionId');

  // Fetch Site Map Data (With Params)
  const siteMapQuery = useSiteMapQuery({
    communityId,
    sessionId,
  });

  // Partner Integration
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data.partnerToken && PARTNER_TOKENS.includes(e.data.partnerToken)) {
        dispatch(setUserData(e.data));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch]);

  // display error on missing URL params
  if (!communityId) {
    return <Result status="error" title="Missing necessary params" />;
  }

  // display error on handled API error
  if (siteMapQuery.error) {
    return <Result status="error" title={siteMapQuery.error.message} />;
  }

  // errors handled, if no data, means query is loading
  if (!siteMapQuery.data) {
    return <LoadingSpinner />;
  }

  // display error when the version doesn't match
  if (!config.version.startsWith(siteMapQuery.data.appInfo.versionNumber)) {
    return (
      <Result
        status="error"
        title="App version mismatch"
        subTitle="Please ask for the right version"
      />
    );
  }

  // Global Styles and Theming
  const {
    displayOptions: [displayOptions],
  } = siteMapQuery.data;

  const theme: DefaultTheme = {
    colors: {
      primaryColor: displayOptions.primaryColor || DEFAULT_PRIMARY_COLOR,
      textColor: displayOptions.primaryTextColor || DEFAULT_PRIMARY_TEXT_COLOR,
      secondaryColor: DEFAULT_SECONDARY_COLOR,
      bgColor: DEFAULT_BG_COLOR,
      highlightColor: displayOptions.highlightColor || DEFAULT_HIGHLIGHT_COLOR,
    },
    font: {
      fontFamily: displayOptions.font || DEFAULT_FONT_FAMILY,
      dimsFontFamily: displayOptions.dimsFont || DEFAULT_FONT_FAMILY,
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <SiteMapPage />
      {currUTourUrl ? <UTourModal /> : currPlanCode ? <FloorPlanModal /> : <LotModal />}
    </ThemeProvider>
  );
}
